import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { services } from '@services/api';
import GroupSelectionDialog from './GroupSelectionDialog';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  MenuRounded as MenuIcon,
  SpaceDashboardRounded as Dashboard,
  ComputerRounded as Computer,
  StorageRounded as Storage,
  CloudQueueRounded as CloudQueue,
  LinkRounded as Link,
  NotificationsNoneRounded as Notifications,
  NotificationsActiveRounded as NotificationsActive,
  BugReportRounded as BugReport,
  LogoutRounded as Logout,
  ExpandLessRounded as ExpandLess,
  ExpandMoreRounded as ExpandMore,
  SpeedRounded as Speed,
  AcUnitRounded as AcUnit,
  SettingsRounded as SettingsIcon,
  AdminPanelSettingsRounded as AdminPanelSettings,
  PersonRemoveRounded as PersonRemove,
  MenuBookRounded as MenuBook,
  RocketLaunchRounded as RocketLaunch,
  WebhookRounded as Webhook,
  DesktopWindowsRounded as DesktopWindows,
  AttachMoneyRounded as AttachMoney,
  SummarizeRounded as Summarize,
  DnsRounded as Dns,
  CloudDoneRounded,
  AccountTreeRounded as AccountTree,
  ChevronRightRounded as ChevronRight,
  OpenInNewRounded as OpenInNew,
  VpnLockRounded as VpnLock,
  KeyboardArrowDownRounded,
  EnhancedEncryption,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@store/store';
import { logout } from '@features/auth/authSlice';
import { useCanSeeDims } from '@hooks/useCanSeeDims';
import { sidebar, neutral, radii, semantic, layout, shadows } from '@styles/tokens';
import { CloudPulseIcon } from '@features/auth/login/BrandMark';
import { GlobalSearch, type SearchTarget } from './GlobalSearch';
import type { AwsAccount } from '../../types/alerts';

const drawerWidth = layout.sidebarWidth;

interface NavExternalLink {
  title: string;
  href: string;
}

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
  /** External pages offered in a floating menu; each opens in a new browser tab. */
  links?: NavExternalLink[];
}

const baseNavItems: NavItem[] = [
  { title: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
  { title: 'EC2 Instances', path: '/ec2', icon: <Computer /> },
  { title: 'RDS Databases', path: '/rds', icon: <Storage /> },
  { title: 'S3 Buckets', path: '/s3', icon: <CloudQueue /> },
  { title: 'S3 Restore', path: '/s3-restore', icon: <AcUnit /> },
  { title: 'URL Monitoring', path: '/urls', icon: <Link /> },
];

/**
 * VPN admin panels. They keep their own login, so the monitor only links to
 * them: each opens in a new browser tab and nothing is embedded here.
 */
const VPN_LINKS: NavExternalLink[] = [
  { title: 'Prod', href: 'https://vpn.datagainservices.com/' },
  { title: 'Non-Prod', href: 'https://vpndev.datagainservices.com/' },
];

/** Page titles for the top bar, keyed by route prefix (longest match wins). */
const ROUTE_TITLES: Array<[string, string]> = [
  ['/dashboard', 'Dashboard'],
  ['/ec2', 'EC2 Instances'],
  ['/rds', 'RDS Databases'],
  ['/s3-restore', 'S3 Restore'],
  ['/s3', 'S3 Buckets'],
  ['/urls', 'URL Monitoring'],
  ['/apm/access', 'APM Access'],
  ['/apm', 'Application Performance'],
  ['/dims-db-monitor', 'DIMS DB Monitor'],
  ['/dims-alerts', 'DIMS DB Alerts'],
  ['/dims', 'DIMS LIS Computers'],
  ['/deployment', 'DIMS Deployment'],
  ['/billing', 'Billing & Cost Management'],
  ['/digest', 'Cost Digest'],
  ['/offboarding', 'AWS User Offboarding'],
  ['/docs', 'Documentation'],
  ['/alerts', 'Alert Configuration'],
  ['/settings/access-control', 'Access Control'],
  ['/settings/webhooks', 'Webhooks'],
  ['/settings', 'Settings'],
];

export const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { unreadCount } = useSelector((state: RootState) => state.notifications);
  const isAdmin = (user?.permissions || []).includes('system:admin');
  const canSeeBilling = (user?.permissions || []).includes('billing:read');
  const canSeeAppInfra = (user?.permissions || []).includes('appinfra:read');
  const canSeeApm = (user?.permissions || []).includes('apm:read');
  // RDP Access is restricted to users explicitly granted rdp:read (the rdp_admin /
  // rdp_requester roles) — deliberately NOT visible to general admins.
  const canSeeRdp = (user?.permissions || []).includes('rdp:read');
  // Bot Scan is no longer a blanket admin page - it has its own permission so it can
  // be granted without handing over system:admin.
  const canSeeBotScan = (user?.permissions || []).includes('botscan:read');
  // DIMS deployment has its own permission - deliberately NOT `|| isAdmin`, so an
  // admin does not silently acquire the ability to replace production.
  const canSeeDeployment = (user?.permissions || []).includes('deploy:read');
  // DB Alerts is granted per person on its own Access tab, like Deployment - not `|| isAdmin`.
  const canSeeDimsAlerts = (user?.permissions || []).includes('dimsalerts:read');
  // Secure Vault: per-person like RDP Access - deliberately NOT `|| isAdmin`.
  const canSeeVault = (user?.permissions || []).includes('vault:use');
  // Cost Digest: per-person like Deployment - deliberately NOT `|| isAdmin`.
  const canSeeDigest = (user?.permissions || []).includes('digest:read');
  // AWS User Offboarding: admins only (offboard:read is seeded onto system:admin
  // roles by migration 051). Kept its own flag, not `isAdmin`, to match the rest.
  const canSeeOffboarding = (user?.permissions || []).includes('offboard:read');
  // Private Documentation page - visible only to the email allowlist (the PDF
  // endpoint itself is email-gated server-side). "sirf mujhe".
  const DOCS_EMAILS = ['shams@datagainservices.com'];
  const canSeeDocs = DOCS_EMAILS.includes((user?.email || '').toLowerCase());
  const { canSeeDims } = useCanSeeDims();

  // The login response carries `name`, but `/auth/users/me` — which is what
  // re-populates the store on every reload — carries `full_name` instead. Fall
  // through both so the header does not degrade to "User" after a refresh.
  const displayName = user?.name || user?.full_name || user?.email?.split('@')[0] || 'User';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [linksMenu, setLinksMenu] = useState<{ anchor: HTMLElement; item: NavItem } | null>(null);
  const [groupSelectionDismissed, setGroupSelectionDismissed] = useState(false);

  const queryClient = useQueryClient();

  // Check if non-admin user has any group memberships
  const { data: myMemberships, isSuccess: membershipsLoaded } = useQuery({
    queryKey: ['my-group-memberships', user?.id],
    queryFn: async () => {
      const res = await services.auth.getMyGroupMemberships();
      return res.data;
    },
    enabled: !isAdmin && !!user?.id,
    staleTime: Infinity,
  });

  /**
   * Connected AWS account, for the sidebar footer. This endpoint already backs
   * the Alert Configuration account picker, so the footer reflects the same
   * account the rest of the app is reading from rather than a second source of
   * truth. Admin-gated, so it fails soft to a hidden block.
   */
  const { data: accounts } = useQuery<AwsAccount[]>({
    queryKey: ['aws-accounts'],
    queryFn: async () => (await services.alerts.getAccounts()).data?.accounts ?? [],
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
  const account = accounts?.find((a) => a.is_default) ?? accounts?.[0] ?? null;

  const showGroupDialog =
    !isAdmin &&
    membershipsLoaded &&
    Array.isArray(myMemberships) &&
    myMemberships.length === 0 &&
    !groupSelectionDismissed;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    queryClient.clear();
    navigate('/login');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const currentTitle =
    ROUTE_TITLES.find(([prefix]) => isActiveRoute(prefix))?.[1] ?? 'AWS Monitor';

  // Nav visibility is permission-driven and must stay that way — a flat
  // hardcoded list would expose admin-only pages to everyone.
  const navItems: NavItem[] = [
    ...baseNavItems,
    // BOTSCAN-DISABLED-APPINFRA: page has no component yet, so the link would 404.
    // ...(canSeeAppInfra
    //   ? [{ title: 'Application Infrastructure', path: '/app-infrastructure', icon: <AccountTree /> }]
    //   : []),
    ...(canSeeApm
      ? [{ title: 'Application Performance', path: '/apm', icon: <AccountTree /> }]
      : []),
    // One DIMS group instead of three scattered entries. Each child keeps its
    // own permission gate, and the group only appears if at least one child is
    // visible - so this is a menu change, not an access change.
    ...(canSeeDims || canSeeDeployment || canSeeDimsAlerts
      ? [
          {
            title: 'DIMS',
            path: '/dims',
            icon: <DesktopWindows />,
            children: [
              ...(canSeeDims
                ? [
                    { title: 'LIS Computers', path: '/dims', icon: <DesktopWindows /> },
                    { title: 'DB Monitor', path: '/dims-db-monitor', icon: <Dns /> },
                  ]
                : []),
              ...(canSeeDimsAlerts
                ? [{ title: 'DB Alerts', path: '/dims-alerts', icon: <NotificationsActive /> }]
                : []),
              ...(canSeeDeployment
                ? [{ title: 'Deployment', path: '/deployment', icon: <RocketLaunch /> }]
                : []),
            ],
          },
        ]
      : []),
    ...(canSeeBilling
      ? [{ title: 'Billing & Cost Management', path: '/billing', icon: <AttachMoney /> }]
      : []),
    ...(canSeeDigest
      ? [{ title: 'Cost Digest', path: '/digest', icon: <Summarize /> }]
      : []),
    ...(isAdmin
      ? [{ title: 'Alert Configuration', path: '/alerts', icon: <NotificationsActive /> }]
      : []),
    ...(canSeeBotScan
      ? [{ title: 'Bot Scan', path: '/botscan', icon: <BugReport /> }]
      : []),
    ...(canSeeRdp
      ? [{ title: 'RDP Access', path: '/rdp-access', icon: <AdminPanelSettings /> }]
      : []),
    ...(canSeeOffboarding
      ? [{ title: 'User Offboarding', path: '/offboarding', icon: <PersonRemove /> }]
      : []),
    ...(canSeeDocs
      ? [{ title: 'Documentation', path: '/docs', icon: <MenuBook /> }]
      : []),
    ...(canSeeVault
      ? [{ title: 'Secure Vault', path: '/vault', icon: <EnhancedEncryption /> }]
      : []),
    ...(isAdmin
      ? [{ title: 'VPN', path: '/vpn', icon: <VpnLock />, links: VPN_LINKS }]
      : []),
    ...(isAdmin
      ? [
          {
            title: 'Settings',
            path: '/settings',
            icon: <SettingsIcon />,
            children: [
              { title: 'Access Control', path: '/settings/access-control', icon: <AdminPanelSettings /> },
              { title: 'Webhooks', path: '/settings/webhooks', icon: <Webhook /> },
            ],
          },
        ]
      : []),
  ];

  // Open the group that contains the current page, so a nested item is never
  // hidden behind a collapsed parent after a reload or a direct link.
  useEffect(() => {
    const owning = navItems.find((item) => item.children?.some((c) => isActiveRoute(c.path)));
    if (owning) {
      setExpandedItems((prev) =>
        prev.includes(owning.title) ? prev : [...prev, owning.title],
      );
    }
    // Only on navigation: re-running on navItems identity would fight a manual collapse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Header search offers exactly what the sidebar offers - built from the
  // already permission-filtered navItems, never from a list of its own.
  const searchTargets: SearchTarget[] = navItems.flatMap((item): SearchTarget[] => {
    if (item.links) return item.links.map((l) => ({ title: `${item.title} · ${l.title}`, href: l.href, group: 'External' }));
    if (item.children) return item.children.map((c) => ({ title: c.title, path: c.path, group: item.title }));
    return [{ title: item.title, path: item.path, group: 'Pages' }];
  });

  const navButtonSx = (active: boolean, nested = false) => ({
    mx: 1,
    mb: 0.375,
    pl: nested ? 4.25 : 1.5,
    pr: 1.25,
    py: 0.875,
    minHeight: 42,
    position: 'relative',
    borderRadius: `${radii.md}px`,
    color: active ? sidebar.activeText : sidebar.text,
    background: active ? sidebar.activeBg : 'transparent',
    boxShadow: active ? sidebar.activeGlow : 'none',
    transition: 'background-color .18s ease, color .18s ease, box-shadow .18s ease',
    ...(active && !nested
      ? {
          '&::before': {
            content: '""', position: 'absolute', left: -8, top: 9, bottom: 9, width: 3,
            borderRadius: '0 3px 3px 0', background: sidebar.activeBar,
          },
        }
      : {}),
    '&.Mui-selected, &.Mui-selected:hover': { background: sidebar.activeBg },
    '&:focus-visible': { outline: `2px solid ${sidebar.activeBar}`, outlineOffset: -2 },
    '&:hover': {
      backgroundColor: active ? undefined : sidebar.hoverBg,
      color: active ? sidebar.activeText : sidebar.textHover,
    },
    '& .MuiListItemIcon-root': {
      minWidth: 34,
      color: active ? sidebar.activeIcon : 'inherit',
      '& svg': { fontSize: 20 },
    },
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
      fontWeight: active ? 600 : 500,
      letterSpacing: '-0.005em',
    },
  });

  const drawer = (
    <Box
      className="scrollbar-dark"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: sidebar.bgGradient,
        color: sidebar.text,
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.25,
          height: layout.headerHeight,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${radii.md}px`,
            background: 'rgba(56,213,245,0.08)',
            border: '1px solid rgba(56,213,245,0.22)',
            flexShrink: 0,
          }}
        >
          <CloudPulseIcon size={26} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '1rem', fontWeight: 800, color: '#fff', lineHeight: 1.25, letterSpacing: '-0.02em' }}
          >
            AWS <Box component="span" sx={{ color: '#38D5F5' }}>Monitor</Box>
          </Typography>
          <Typography
            noWrap
            sx={{ fontSize: '0.6875rem', fontWeight: 500, color: sidebar.caption }}
          >
            DataGain Services
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: sidebar.border }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
        <Typography
          sx={{
            px: 2.5,
            pb: 0.75,
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: sidebar.caption,
          }}
        >
          Monitoring
        </Typography>
        <List disablePadding>
          {navItems.map((item) => {
            const active = isActiveRoute(item.path);
            return (
              <React.Fragment key={item.title}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={active}
                    aria-haspopup={item.links ? 'menu' : undefined}
                    aria-expanded={item.links ? linksMenu?.item.title === item.title : undefined}
                    onClick={(event) => {
                      if (item.links) {
                        setLinksMenu({ anchor: event.currentTarget, item });
                      } else if (item.children) {
                        toggleExpanded(item.title);
                      } else {
                        handleNavClick(item.path);
                      }
                    }}
                    sx={navButtonSx(active)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.title} />
                    {item.links && <ChevronRight sx={{ fontSize: 18 }} />}
                    {item.children &&
                      (expandedItems.includes(item.title) ? (
                        <ExpandLess sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 18 }} />
                      ))}
                  </ListItemButton>
                </ListItem>
                {item.children && (
                  <Collapse in={expandedItems.includes(item.title)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        const childActive = isActiveRoute(child.path);
                        return (
                          <ListItemButton
                            key={child.title}
                            selected={childActive}
                            onClick={() => handleNavClick(child.path)}
                            sx={navButtonSx(childActive, true)}
                          >
                            <ListItemIcon>{child.icon}</ListItemIcon>
                            <ListItemText primary={child.title} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Connected AWS account */}
      {account && (
        <Box sx={{ p: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: `${radii.md}px`,
              backgroundColor: sidebar.footerBg,
              border: `1px solid ${sidebar.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <CloudDoneRounded sx={{ fontSize: 15, color: semantic.success.light }} />
              <Typography
                sx={{
                  fontSize: '0.5875rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: sidebar.caption,
                }}
              >
                AWS Account
              </Typography>
            </Box>
            <Typography
              className="tabular-nums"
              noWrap
              sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#fff' }}
              title={account.account_id}
            >
              {account.label || account.account_id}
            </Typography>
            <Chip
              label={account.region}
              size="small"
              sx={{
                mt: 0.75,
                height: 19,
                fontSize: '0.625rem',
                fontWeight: 600,
                color: sidebar.text,
                backgroundColor: 'rgba(148,163,184,0.14)',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: neutral[50] }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${neutral[200]}`,
          boxShadow: shadows.xs,
          color: neutral[900],
        }}
      >
        <Toolbar sx={{ minHeight: `${layout.headerHeight}px !important`, px: { xs: 2, sm: 3 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, display: { md: 'none' }, color: 'text.secondary' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            noWrap
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'text.primary',
              display: { xs: 'block', md: 'none' },
            }}
          >
            {currentTitle}
          </Typography>

          <Box sx={{ display: { xs: 'none', sm: 'block' }, ml: { md: 0.5 } }}>
            <GlobalSearch targets={searchTargets} onNavigate={handleNavClick} />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Real-time metrics" arrow>
            <IconButton
              aria-label="real-time metrics"
              onClick={() => navigate('/ec2/metrics')}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <Speed />
            </IconButton>
          </Tooltip>

          <Tooltip title="Notifications" arrow>
            <IconButton
              aria-label="notifications"
              onClick={() => navigate('/notifications')}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1.5, my: 1.75 }} />

          <IconButton
            onClick={handleProfileMenuOpen}
            size="small"
            aria-label="account menu"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            sx={{ gap: 1, borderRadius: `${radii.md}px`, px: 0.75, py: 0.5, '&:hover': { backgroundColor: neutral[100] } }}
          >
            <Avatar
              src={user?.picture_url || user?.avatar_url || undefined}
              alt=""
              imgProps={{ referrerPolicy: 'no-referrer' }}
              sx={{
                width: 34,
                height: 34,
                background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                fontSize: '0.8125rem',
                fontWeight: 700,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, maxWidth: 160 }}
              >
                {displayName}
              </Typography>
              <Typography
                noWrap
                sx={{ fontSize: '0.6875rem', color: 'text.secondary', lineHeight: 1.3, maxWidth: 160 }}
              >
                {user?.email || ''}
              </Typography>
            </Box>
            <KeyboardArrowDownRounded sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: 'text.secondary' }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: sidebar.bg,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: sidebar.bg,
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          mt: `${layout.headerHeight}px`,
          minHeight: `calc(100vh - ${layout.headerHeight}px)`,
          backgroundColor: neutral[50],
          // MainLayout owns the page gutter. Pages must not add their own
          // top-level padding or the spacing drifts from screen to screen.
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        <Box sx={{ maxWidth: layout.contentMaxWidth, mx: 'auto', width: '100%' }}>
          <Outlet />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
      >
        <Box sx={{ px: 1.75, py: 1.25 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
            {displayName}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', wordBreak: 'break-all' }}>
            {user?.email || ''}
          </Typography>
        </Box>
        <Divider />
        {/* Removed Profile and Settings from profile dropdown as requested */}
        <MenuItem onClick={handleLogout} sx={{ mt: 0.5, color: semantic.error.text }}>
          <Logout sx={{ mr: 1.25, fontSize: 18 }} />
          Logout
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={linksMenu?.anchor ?? null}
        open={Boolean(linksMenu)}
        onClose={() => setLinksMenu(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 230, ml: 0.75 } } }}
      >
        {(linksMenu?.item.links ?? []).map((link) => (
          <MenuItem
            key={link.href}
            component="a"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setLinksMenu(null);
              if (isMobile) {
                setMobileOpen(false);
              }
            }}
          >
            <ListItemText
              primary={link.title}
              secondary={new URL(link.href).host}
              primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
            <OpenInNew sx={{ ml: 2, fontSize: 16, color: 'text.secondary' }} />
          </MenuItem>
        ))}
      </Menu>
      <GroupSelectionDialog
        open={showGroupDialog}
        onComplete={() => setGroupSelectionDismissed(true)}
      />
    </Box>
  );
};
